import { WebGrpcDecodedDataSchema } from '@terra/shared-schemas-tracker';
import { z } from 'zod';
import { ToolExecutionSourceSchema } from './proxy-context.dto';

/**
 * HAR Entry schema for tool execution tracking
 * This is stored separately from regular HAR tracking to associate with tool calls
 */
export const ToolExecutionHarPayloadSchema = z.object({
  // Execution context
  threadId: z.string().describe('AI graph thread ID'),
  toolCallId: z.string().describe('Specific tool call ID'),
  executionId: z.string().describe('Execution ID'),
  sessionId: z.string().describe('Session identifier'),

  // Application context
  applicationId: z.string(),
  tenantId: z.string().optional(),
  credentialId: z.string().optional(),
  matchedCredentialId: z.string().optional(),

  // User context (from gateway)
  userId: z.string().optional().describe('User ID from authentication context'),
  tenantDomain: z
    .string()
    .optional()
    .describe('Tenant domain from authentication context'),
  roles: z
    .array(z.string())
    .optional()
    .describe('User roles from authentication context'),
  permissions: z
    .array(z.string())
    .optional()
    .describe('User permissions from authentication context'),

  // HAR data (standard HAR Entry format)
  harEntry: z.object({
    request: z.object({
      method: z.string(),
      url: z.string(),
      httpVersion: z.string(),
      cookies: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          path: z.string().optional(),
          domain: z.string().optional(),
          expires: z.string().optional(),
          httpOnly: z.boolean().optional(),
          secure: z.boolean().optional(),
          comment: z.string().optional(),
        }),
      ),
      headers: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          comment: z.string().optional(),
        }),
      ),
      queryString: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          comment: z.string().optional(),
        }),
      ),
      postData: z
        .object({
          mimeType: z.string(),
          params: z
            .array(
              z.object({
                name: z.string(),
                value: z.string().optional(),
                fileName: z.string().optional(),
                contentType: z.string().optional(),
                comment: z.string().optional(),
              }),
            )
            .optional(),
          text: z.string().optional(),
          comment: z.string().optional(),
        })
        .optional(),
      headersSize: z.number(),
      bodySize: z.number(),
      comment: z.string().optional(),
    }),
    response: z.object({
      status: z.number(),
      statusText: z.string(),
      httpVersion: z.string(),
      cookies: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          path: z.string().optional(),
          domain: z.string().optional(),
          expires: z.string().optional(),
          httpOnly: z.boolean().optional(),
          secure: z.boolean().optional(),
          comment: z.string().optional(),
        }),
      ),
      headers: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          comment: z.string().optional(),
        }),
      ),
      content: z.object({
        size: z.number(),
        mimeType: z.string(),
        text: z.string().optional().nullable(),
        encoding: z.enum(['utf8', 'base64']).optional(),
        comment: z.string().optional(),
      }),
      redirectURL: z.string(),
      headersSize: z.number(),
      bodySize: z.number(),
      comment: z.string().optional(),
      _transferSize: z.number().nullable().optional(),
    }),
    timings: z.object({
      blocked: z.number().optional(),
      dns: z.number().optional(),
      connect: z.number().optional(),
      send: z.number().optional(),
      wait: z.number(),
      receive: z.number(),
      ssl: z.number().optional(),
      comment: z.string().optional(),
    }),
    startedDateTime: z.string(),
    time: z.number(),
    cache: z
      .object({
        beforeRequest: z.record(z.string(), z.unknown()).optional(),
        afterRequest: z.record(z.string(), z.unknown()).optional(),
        comment: z.string().optional(),
      })
      .optional(),
    serverIPAddress: z.string().optional(),
    connection: z.string().optional(),
    comment: z.string().optional(),
    webGrpcDecodedData: z
      .lazy(() => WebGrpcDecodedDataSchema)
      .nullable()
      .optional(),
  }),

  source: ToolExecutionSourceSchema,
  executingCloudService: z.string().min(1).optional(),

  toolType: z.enum(['HTTP', 'PYTHON']),
  toolName: z.string(),
  timestamp: z.string(),
  parentToolCallId: z.string().optional(),
  redirectChainId: z
    .string()
    .optional()
    .describe(
      'Identifier shared by every HAR captured while following one redirect chain for the same request.',
    ),
  redirectHopIndex: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe(
      'Zero-based position of this HAR within its redirect chain, ordered from the initial request to the final response.',
    ),
});

export type ToolExecutionHarPayloadDto = z.infer<
  typeof ToolExecutionHarPayloadSchema
>;

export const ToolExecutionHarResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  harEventId: z.string().optional().describe('ID of the stored HAR event'),
});

export type ToolExecutionHarResponseDto = z.infer<
  typeof ToolExecutionHarResponseSchema
>;
